import { themeConfig } from './config/theme.config';
import { domainConfigs, AppConfig } from './config/domains';

// Helper to get current domain
const getHostname = () => {
  if (typeof window !== 'undefined') {
    const override = localStorage.getItem('domainOverride');
    if (override) return override;
    return window.location.hostname;
  }
  return 'default';
};

// Get the merged configuration for the current domain
export const getConfig = (): AppConfig => {
  const hostname = getHostname();
  const baseConfig = domainConfigs['default'] as AppConfig;
  const domainConfig = domainConfigs[hostname] || {};
  
  return {
    ...baseConfig,
    ...domainConfig
  };
};

// Export config as a Proxy so existing imports work without changes
export const config = new Proxy({} as AppConfig, {
  get: (target, prop) => {
    const currentConfig = getConfig();
    return currentConfig[prop as keyof AppConfig];
  }
});

// Template configuration mapping
const TEMPLATE_MAPPING: Record<string, string> = {
  flysync: 'flysync',
  travelOne: 'travelOne',
  Travelone: 'travelOne',
  Helinto: 'Helinto',
  makevoyage: 'makevoyage',
  Makevoyage: 'makevoyage',
  Tripbng: 'Tripbng',
  default: 'default',
  corporate: 'corporate',
  Rajesh: 'default', // Rajesh uses default template
  DMCLeisure: 'default', // DMCLeisure uses default template
};

// Corporate name to theme mapping
const CORPORATE_TO_THEME_MAPPING: Record<string, string> = {
  flysync: 'flysync',
  travelOne: 'travelOne',
  Travelone: 'travelOne',
  Helinto: 'Helinto',
  makevoyage: 'makevoyage',
  Makevoyage: 'makevoyage',
  Rajesh: 'Rajesh',
  Tripbng: 'Tripbng',
  DMCLeisure: 'DMCLeisure',
  default: 'flysync', // Default fallback theme
};

// Asset folder mapping for templates
const ASSET_FOLDER_MAPPING: Record<string, string> = {
  flysync: 'flysync',
  travelOne: 'Travelone',
  Travelone: 'Travelone',
  Helinto: 'Helinto',
  makevoyage: 'Makevoyage',
  Makevoyage: 'Makevoyage',
  Tripbng: 'Tripbng',
  default: 'default',
  corporate: 'corporate',
};

// Get current template name with fallback
export const getCurrentTemplateName = (): string => {
  const templateName = config.templateConfig || 'template1';
  return TEMPLATE_MAPPING[templateName] || templateName?.toLowerCase();
};

// Get current theme based on corporate name
export const getCurrentTheme = () => {
  const corporateName = config.corporateName || 'flysync';
  const themeName = CORPORATE_TO_THEME_MAPPING[corporateName] || 'flysync';
  return (
    themeConfig[themeName as keyof typeof themeConfig] || themeConfig.flysync
  );
};

// Get theme name from corporate name
export const getThemeNameFromCorporate = (corporateName?: string): string => {
  const name = corporateName || config.corporateName || 'flysync';
  return CORPORATE_TO_THEME_MAPPING[name] || 'flysync';
};

// Get available themes/corporates
export const getAvailableThemes = () => {
  return Object.keys(themeConfig);
};

// Get current corporate name (alias for backward compatibility)
export const getCurrentCorporateName = () => {
  return getCurrentTemplateName();
};

// Get asset folder for current template
export const getCurrentAssetFolder = (): string => {
  const templateName = getCurrentTemplateName();
  return ASSET_FOLDER_MAPPING[templateName] || templateName;
};

// Get template-specific asset path
export const getTemplateAssetPath = (
  assetType: 'logo' | 'favicon' | 'bg' | 'loginBanner' | 'loader',
): string => {
  const corporateName = config.corporateName || 'flysync';
  switch (assetType) {
    case 'logo':
      return `/${corporateName}/logo.png`;
    case 'favicon':
      return `/${corporateName}/favicon.ico`;
    case 'bg':
      return '';
    case 'loginBanner':
      return '';
    case 'loader':
      return `/${corporateName}/loader.gif`;
    default:
      return '';
  }
};

// Validate template configuration
export const validateTemplateConfig = (): {
  isValid: boolean;
  templateName: string;
  assetFolder: string;
  theme: any;
  errors: string[];
} => {
  const errors: string[] = [];
  const templateName = getCurrentTemplateName();
  const assetFolder = getCurrentAssetFolder();
  const theme = getCurrentTheme();

  if (
    !themeConfig[templateName as keyof typeof themeConfig] &&
    !themeConfig[ASSET_FOLDER_MAPPING[templateName] as keyof typeof themeConfig]
  ) {
    errors.push(`Template '${templateName}' not found in theme configuration`);
  }

  if (
    !ASSET_FOLDER_MAPPING[templateName] &&
    !ASSET_FOLDER_MAPPING[templateName.toLowerCase()]
  ) {
    errors.push(
      `Asset folder mapping not found for template '${templateName}'`,
    );
  }

  return {
    isValid: errors.length === 0,
    templateName,
    assetFolder,
    theme,
    errors,
  };
};
